import { useState, useEffect, useCallback } from "react";
import { Link, useLoaderData } from "react-router-dom";
import { analyticsAPI } from "../../lib/api";
import { Header } from "../../components/layout/Header";
import { DateRangePicker } from "../../components/analytics/DateRangePicker";
import { StatCard } from "../../components/analytics/StatCard";
import type { EpisodeAnalyticsData } from "../../routes/loaders";
import type { EpisodeStats } from "../../types/analytics";
import styles from "./EpisodeAnalyticsPage.module.css";

function defaultFrom(): Date {
  return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
}

export default function EpisodeAnalyticsPage() {
  const { episode, podcast, organizationId } = useLoaderData() as EpisodeAnalyticsData;

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<EpisodeStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await analyticsAPI.episode(organizationId, podcast.id, episode.id, from, to);
      setStats(s);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [organizationId, podcast.id, episode.id, from, to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange = useCallback((newFrom: Date, newTo: Date) => {
    setFrom(newFrom);
    setTo(newTo);
  }, []);

  return (
    <>
      <Header />
      <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.breadcrumb}>
          <Link to={`/organizations/${organizationId}/podcasts`}>Podcasts</Link>
          <span className={styles.sep}>/</span>
          <Link to={`/organizations/${organizationId}/podcasts/${podcast.id}`}>{podcast.name}</Link>
          <span className={styles.sep}>/</span>
          <Link to={`/organizations/${organizationId}/podcasts/${podcast.id}/analytics`}>
            Analytics
          </Link>
          <span className={styles.sep}>/</span>
          <span>{episode.title}</span>
        </div>
        <h1 className={styles.title}>{episode.title}</h1>
        <p className={styles.subtitle}>Episode Analytics</p>
      </div>

      <DateRangePicker from={from} to={to} onChange={handleDateChange} />

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading analytics…</div>
      ) : (
        <div className={styles.statGrid}>
          <StatCard label="Downloads" value={stats?.downloads ?? 0} />
          <StatCard label="Streams" value={stats?.streams ?? 0} />
          <StatCard label="Completions" value={stats?.completions ?? 0} />
          <StatCard
            label="Completion Rate"
            value={`${((stats?.completionRate ?? 0) * 100).toFixed(1)}%`}
          />
        </div>
      )}
    </div>
    </>
  );
}
